import { motion } from "framer-motion";
import { ArrowRight, Calendar, MapPin } from "lucide-react";
import heroImage from "@/assets/hero-gymnastics.webp";
import winnersCupLogo from "@/assets/winners-cup-logo.png";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";

const TARGET_DATE = new Date("2027-02-26T08:00:00");

const useCountdown = (targetDate: Date) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    const calc = () => {
      const diff = targetDate.getTime() - Date.now();
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((diff / 1000 / 60) % 60),
          seconds: Math.floor((diff / 1000) % 60),
        });
      }
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [targetDate]);
  return timeLeft;
};

const TICKER = [
  "Winner's Cup 2027",
  "Feb 26 — 28",
  "Ontario, CA",
  "USAG Sanctioned",
  "Levels 2 — 10 + Xcel",
  "Vault · Bars · Beam · Floor",
  "West Coast's Premier Meet",
  "Chalk Up ✦ Compete ✦ Rise",
];

const Hero = () => {
  const timeLeft = useCountdown(TARGET_DATE);

  const container = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.09, delayChildren: 0.2 } },
  };
  const item = {
    hidden: { opacity: 0, y: 24, filter: "blur(8px)" },
    visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] as const } },
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-secondary scanlines">
      {/* Background image */}
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 1.2 }}
        animate={{ scale: 1.05 }}
        transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <img
          src={heroImage}
          alt="Winner's Cup 2027 gymnastics"
          width={1920}
          height={1280}
          className="w-full h-full object-cover object-[center_45%] grayscale-[40%]"
          decoding="async"
          {...({ fetchpriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>)}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/95 via-secondary/80 to-secondary" />
        <div className="absolute inset-0 bg-spotlight" />
      </motion.div>

      {/* Grid overlay */}
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />

      {/* Sunset orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full blur-2xl"
          style={{ background: "radial-gradient(circle, hsl(28 100% 58% / 0.35) 0%, transparent 70%)" }}
        />
        <div
          className="absolute -bottom-56 -left-40 w-[720px] h-[720px] rounded-full blur-2xl"
          style={{ background: "radial-gradient(circle, hsl(330 92% 58% / 0.28) 0%, transparent 70%)" }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[480px] h-[480px] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, hsl(252 75% 60% / 0.22) 0%, transparent 70%)" }}
        />
      </div>

      {/* Top sunset bar */}
      <motion.div
        className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-sunset z-20"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.4, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformOrigin: "left" }}
      />

      {/* Cinematic side rails */}
      <div className="absolute top-0 bottom-0 left-4 sm:left-8 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent z-10 hidden md:block" />
      <div className="absolute top-0 bottom-0 right-4 sm:right-8 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent z-10 hidden md:block" />

      {/* Content */}
      <div className="relative z-10 site-container text-center pt-24 sm:pt-28 pb-32 chalk-dust">
        <motion.div variants={container} initial="hidden" animate="visible">
          {/* Logo */}
          <motion.div variants={item} className="mb-6">
            <img
              src={winnersCupLogo}
              alt="Winner's Cup"
              className="w-64 sm:w-80 md:w-[28rem] lg:w-[34rem] h-auto mx-auto"
              style={{ filter: "drop-shadow(0 0 18px hsl(18 100% 60% / 0.55)) drop-shadow(0 0 40px hsl(330 92% 58% / 0.3))" }}
              decoding="async"
            />
          </motion.div>

          {/* SEO-only h1 */}
          <h1 className="sr-only">Winner's Cup 2027</h1>

          {/* Year */}
          <motion.div variants={item} className="mb-8 flex items-center justify-center gap-3 sm:gap-5">
            <span className="h-px flex-1 max-w-[120px] bg-gradient-to-r from-transparent to-primary/60" />
            <span className="display-hero text-gradient text-6xl sm:text-8xl md:text-9xl tabular">2027</span>
            <span className="h-px flex-1 max-w-[120px] bg-gradient-to-l from-transparent to-primary/60" />
          </motion.div>

          {/* Date / location */}
          <motion.div variants={item} className="mb-10 space-y-2">
            <div className="flex items-center justify-center gap-2 text-foreground text-lg sm:text-xl font-condensed font-semibold tracking-wider uppercase">
              <Calendar className="w-5 h-5 text-primary" />
              <span>February 26 — 28, 2027</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-muted-foreground font-condensed tracking-wider uppercase text-sm">
              <MapPin className="w-4 h-4 text-primary/70" />
              <span>Ontario Convention Center · California</span>
            </div>
          </motion.div>

          {/* Countdown */}
          <motion.div variants={item} className="grid grid-cols-4 gap-2 sm:gap-4 max-w-sm sm:max-w-md mx-auto mb-10" aria-label="Countdown to Winner's Cup 2027">
            {[
              { value: timeLeft.days, label: "Days" },
              { value: timeLeft.hours, label: "Hours" },
              { value: timeLeft.minutes, label: "Min" },
              { value: timeLeft.seconds, label: "Sec" },
            ].map((it) => (
              <div key={it.label} className="flex flex-col items-center">
                <div className="chalk-fx glass border-t-2 border-primary/60 p-3 sm:p-4 w-full">
                  <span className="display-hero text-3xl sm:text-5xl text-foreground tabular">{String(it.value).padStart(2, "0")}</span>
                </div>
                <span className="text-muted-foreground text-[10px] sm:text-xs mt-2 uppercase tracking-[0.3em] font-condensed">{it.label}</span>
              </div>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div variants={item} className="flex flex-col sm:flex-row items-center justify-center gap-5">
            <Link to="/pre-register">
              <motion.button
                className="chalk-fx bg-gradient-sunset text-white px-10 sm:px-14 py-4 sm:py-5 font-condensed font-bold text-base sm:text-lg uppercase tracking-[0.2em] shadow-glow relative overflow-hidden group"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative z-10 flex items-center gap-3">
                  Save the Date
                  <ArrowRight className="w-5 h-5" />
                </span>
              </motion.button>
            </Link>
            <Link
              to="/event"
              className="text-muted-foreground hover:text-foreground font-condensed uppercase tracking-[0.25em] text-sm border-b border-border hover:border-primary pb-1 transition-colors"
            >
              Explore the Event
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom fade above ticker */}
      <div className="absolute bottom-12 left-0 right-0 h-24 bg-gradient-to-t from-secondary to-transparent pointer-events-none" />

      {/* Marquee ticker */}
      <div className="absolute bottom-0 left-0 right-0 z-20 border-t border-border bg-secondary/80 backdrop-blur-sm overflow-hidden py-3">
        <div className="flex animate-marquee whitespace-nowrap font-condensed uppercase tracking-[0.3em] text-muted-foreground text-xs sm:text-sm">
          {[...TICKER, ...TICKER, ...TICKER].map((t, i) => (
            <span key={i} className="ticker-divider">{t}</span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
